"use client"

import { useState, useEffect } from "react"
import { collection, query, where, onSnapshot, doc, updateDoc, arrayUnion, getDoc, setDoc } from "firebase/firestore"
import { onAuthStateChanged } from "firebase/auth"
import { db, auth } from "../firebase"
import { FaBell, FaCheck, FaTimes, FaUser, FaClock, FaCheckCircle } from "react-icons/fa"

const AdminNotifications = () => {
  const [user, setUser] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [adminRequests, setAdminRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [processingRequest, setProcessingRequest] = useState(null)

  const allowedAdmins = [
    "sivapratap2004@gmail.com",
    "iiche@cb.amrita.edu",
    "ananyasai.paruchuri@gmail.com",
    "cb.en.u4che22033@cb.students.amrita.edu",
    "sahithiuppari469@gmail.com",
  ]

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser)
      if (!currentUser?.email || !allowedAdmins.includes(currentUser.email)) {
        setLoading(false)
        return
      }

      // Listen to notifications for current admin
      const notificationsQuery = query(
        collection(db, "adminNotifications"),
        where("adminEmail", "==", currentUser.email),
        where("read", "==", false),
      )

      const unsubscribeNotifications = onSnapshot(notificationsQuery, (snapshot) => {
        const notificationsList = []
        snapshot.forEach((doc) => {
          notificationsList.push({ id: doc.id, ...doc.data() })
        })
        setNotifications(notificationsList)
      })

      // Listen to pending admin requests
      const requestsQuery = query(collection(db, "adminRequests"), where("status", "==", "pending"))

      const unsubscribeRequests = onSnapshot(requestsQuery, (snapshot) => {
        const requestsList = []
        snapshot.forEach((doc) => {
          requestsList.push({ id: doc.id, ...doc.data() })
        })
        setAdminRequests(requestsList)
        setLoading(false)
      })

      return () => {
        unsubscribeNotifications()
        unsubscribeRequests()
      }
    })

    return () => unsubscribeAuth()
  }, [])

  const handleApproveRequest = async (requestEmail) => {
    if (!user?.email) return

    setProcessingRequest(requestEmail)

    try {
      // Update admin request status
      await updateDoc(doc(db, "adminRequests", requestEmail), {
        status: "approved",
        approvedBy: user.email,
        approvedAt: new Date(),
      })

      // Add to allowed admins list
      const approvedAdminsDoc = doc(db, "settings", "approvedAdmins")
      const docSnap = await getDoc(approvedAdminsDoc)

      if (docSnap.exists()) {
        await updateDoc(approvedAdminsDoc, {
          emails: arrayUnion(requestEmail),
        })
      } else {
        await setDoc(approvedAdminsDoc, {
          emails: [requestEmail],
        })
      }

      // Mark related notifications as read
      const relatedNotifications = notifications.filter((n) => n.requestEmail === requestEmail)
      for (const notification of relatedNotifications) {
        await updateDoc(doc(db, "adminNotifications", notification.id), {
          read: true,
        })
      }
    } catch (error) {
      console.error("Error approving request:", error)
    } finally {
      setProcessingRequest(null)
    }
  }

  const handleRejectRequest = async (requestEmail) => {
    if (!user?.email) return

    setProcessingRequest(requestEmail)

    try {
      // Update admin request status
      await updateDoc(doc(db, "adminRequests", requestEmail), {
        status: "rejected",
        approvedBy: user.email,
        approvedAt: new Date(),
      })

      // Mark related notifications as read
      const relatedNotifications = notifications.filter((n) => n.requestEmail === requestEmail)
      for (const notification of relatedNotifications) {
        await updateDoc(doc(db, "adminNotifications", notification.id), {
          read: true,
        })
      }
    } catch (error) {
      console.error("Error rejecting request:", error)
    } finally {
      setProcessingRequest(null)
    }
  }

  const markNotificationAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, "adminNotifications", notificationId), {
        read: true,
      })
    } catch (error) {
      console.error("Error marking notification as read:", error)
    }
  }

  if (!user?.email || !allowedAdmins.includes(user.email)) {
    return null
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Notifications Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaBell className="h-5 w-5 text-green-600" />
          <h2 className="text-xl font-semibold">Admin Notifications</h2>
          {notifications.length > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
              {notifications.length}
            </span>
          )}
        </div>
      </div>

      {/* Pending Admin Requests */}
      {adminRequests.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaUser className="h-4 w-4" />
              Pending Admin Access Requests
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {adminRequests.map((request) => (
              <div
                key={request.id}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
                    <FaUser className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{request.email}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                      <FaClock className="h-3 w-3" />
                      Requested {request.requestedAt?.toDate?.()?.toLocaleDateString() || "recently"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleApproveRequest(request.email)}
                    disabled={processingRequest === request.email}
                    className="inline-flex items-center px-3 py-1.5 border border-green-600 text-sm font-medium rounded-md text-green-600 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingRequest === request.email ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                    ) : (
                      <>
                        <FaCheck className="h-3 w-3 mr-1" />
                        Approve
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleRejectRequest(request.email)}
                    disabled={processingRequest === request.email}
                    className="inline-flex items-center px-3 py-1.5 border border-red-600 text-sm font-medium rounded-md text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingRequest === request.email ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                    ) : (
                      <>
                        <FaTimes className="h-3 w-3 mr-1" />
                        Reject
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Notifications */}
      {notifications.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Notifications</h3>
          </div>
          <div className="p-6 space-y-3">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="flex items-start justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-full">
                    <FaBell className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{notification.title}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{notification.message}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {notification.createdAt?.toDate?.()?.toLocaleString() || "Recently"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => markNotificationAsRead(notification.id)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <FaCheck className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No notifications */}
      {notifications.length === 0 && adminRequests.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-center">
            <FaCheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No pending notifications or admin requests at this time.</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminNotifications
